import { useApolloClient } from "@apollo/client/react";
import type { PlainDateString } from "@sa-nu/shared-utils/date";
import { toPlainDateString, PlainDate } from "@sa-nu/shared-utils/date";
import { parsePlainDate } from "@sa-nu/shared-utils/date";
import clsx from "clsx";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo } from "react";

import type { FragmentType } from "../../../../__generated__/gql";
import { graphql, useFragment } from "../../../../__generated__/gql";
import type {
  AccommodationReservationMutationScope,
  AccommodationReservationProvider,
  OccupancyLimitTargetType,
} from "../../../../__generated__/gql/graphql";
import type { AccommodationFacilityType } from "../../../../__generated__/gql/graphql";
import { PAGES_WITH_UNSTICKY_HEADER_IN_MOBILE } from "../../../../constants";
import { useGtm } from "../../../../lib/google-tag-manager/gtm";
import { buildAccommodationReservationProviderInput } from "../../../../utils";
import { useInNativeApp } from "../../../_hooks/use-in-native-app";
import type { AccommodationNoticeForUser } from "../../accommodation/accommodation-notices";
import { ReservablePeriodExtendedNotice } from "../../transition/rule-revision-2027/reservable-period-extended-notice";

import {
  useCalendarData,
  type AreasOnReservationFormQueryData,
} from "./_hooks/use-calendar-data";
import { useCICO, computeCenteredFirstNight } from "./_hooks/use-ci-co";
import { useReservationCalendarDateRange } from "./_hooks/use-reservation-calendar-date-range";
import { CalendarAreaList } from "./areas/calendar-area-list";
import type { BlockWithUser } from "./areas/cell/block-hover-card-wrapper";
import { useCheckOutDateSelectionModal } from "./check-out-date-selection-modal";
import { CICODateSelectionModalProvider } from "./ci-co-date-selection-modal";
import {
  NONE_DATE_TYPE,
  type DateCellDateType,
} from "./date-navigation/date-cell";
import { DateNavigation } from "./date-navigation/date-navigation";
import { AccommodationFilterProvider } from "./filters/accommodation-filter-context";
import { FilterBar } from "./filters/filter-bar";
import { useReservationFlowOverride } from "./reservation-flow-override-context";

export type ProviderOrLimitType =
  | AccommodationReservationProvider
  | OccupancyLimitTargetType;

export type CICOSelection = {
  checkIn: PlainDate;
  checkOut: PlainDate; // checkout日 = lastNight + 1
};

export type SelectedAccommodationReservation = {
  firstNight: PlainDate;
  lastNight: PlainDate;
  accommodationId: string;
  accommodationFacilities: AccommodationFacilityType[];
};

export type AvailabilityCellClickHandler = (value: {
  date: PlainDate;
  accommodationId: string;
  accommodationName: string;
  accommodationFacilities: AccommodationFacilityType[];
  accommodationNotices: AccommodationNoticeForUser[];
  selected: SelectedAccommodationReservation | null;
}) => void;

export type ReservationCalendarNavigationSize = 7 | 14 | 21;

export const reservationCalendarGridColumnStyles: {
  [key in ReservationCalendarNavigationSize]: { columns: string; span: string };
} = {
  7: {
    columns: "grid-cols-[repeat(8,1fr)]",
    span: "col-span-7",
  },
  14: {
    columns: "grid-cols-[72px_repeat(14,1fr)]",
    span: "col-[span_14/span_14]",
  },
  21: {
    columns: "grid-cols-[72px_repeat(21,1fr)]",
    span: "col-[span_21/span_21]",
  },
};

const OriginalAccommodationReservationFragment = graphql(`
  fragment OriginalAccommodationReservationOnReservationCalendar on AccommodationReservation {
    id
    firstNight
    lastNight
    accommodation {
      id
      site {
        id
        area {
          id
        }
      }
    }
    mutationScope
    ...OriginalAccommodationReservationOnAvailabilityCell
  }
`);

type MaybeViewer = AreasOnReservationFormQueryData["maybeViewer"];
export type UserSubscription = Exclude<
  MaybeViewer,
  null | undefined
>["subscription"];

type ReservationCalendarProps = {
  providerOrLimit: ProviderOrLimitType;
  selected: SelectedAccommodationReservation | null;
  originalReservationData: FragmentType<
    typeof OriginalAccommodationReservationFragment
  > | null;
  minNight: PlainDate;
  maxNight: PlainDate;
  isInternal?: boolean;
  isSampleMode?: boolean;
  initialDate?: PlainDate;
  setSelected: (value: SelectedAccommodationReservation) => void;
};

/**
 * 予約用のカレンダー
 * 与えられたProviderやデータに応じて予約可能な日付を表示し、選択された宿泊在庫を提供する
 *
 * nuqs で URL と双方向同期しているクエリパラメータ:
 * - `ci` / `co` — チェックイン/チェックアウト日（PlainDate）
 * - `area` — エリアフィルタ
 * - `facility-filter` — 設備フィルタ（サウナ・温泉等）
 * - `arch` — 建築タイプフィルタ（MOSS・Large等）
 */
export function ReservationCalendar({
  providerOrLimit,
  selected,
  originalReservationData,
  minNight,
  maxNight,
  isInternal = false,
  isSampleMode = false,
  initialDate,
  setSelected,
}: ReservationCalendarProps) {
  const occupancyLimitTarget =
    typeof providerOrLimit === "string" ? providerOrLimit : null;
  const provider = typeof providerOrLimit === "string" ? null : providerOrLimit;
  const providerFlat = useMemo(
    () =>
      provider ? buildAccommodationReservationProviderInput(provider) : null,
    [provider],
  );

  const originalReservation = useFragment(
    OriginalAccommodationReservationFragment,
    originalReservationData,
  );

  const {
    firstNight,
    lastNight,
    setFirstNight,
    jumpTo,
    navigationSize,
    debouncedFirstNight,
    debouncedLastNight,
    unsettledFirstNightDelta,
    onSettleRef,
  } = useReservationCalendarDateRange({ minNight, maxNight, initialDate });

  const { cico, setCICO, shiftCICO, clearCICO, nextCICO } = useCICO({
    minNight,
    maxNight,
    navigationSize,
    unsettledFirstNightDelta,
    onInit: (initialCICO) => {
      jumpTo(computeCenteredFirstNight(initialCICO, navigationSize));
    },
  });

  // シェブロン操作で firstNight が移動した後、debounce settle 時に CICO も同じ日数だけシフトする。
  // useReservationCalendarDateRange と useCICO は互いを直接参照できない（循環依存）ため、
  // ref 経由でコールバックを渡して接続している。
  useEffect(() => {
    onSettleRef.current = (delta) => {
      if (delta !== 0) shiftCICO(delta);
    };
  }, [onSettleRef, shiftCICO]);

  // UI Wiring
  const handleChevronNavigate = useCallback(
    (deltaDays: number) => {
      setFirstNight((prev) => prev.add({ days: deltaDays }));
    },
    [setFirstNight],
  );

  const handleCICOConfirm = useCallback(
    (newCICO: CICOSelection) => {
      setCICO(newCICO);
      jumpTo(computeCenteredFirstNight(newCICO, navigationSize));
    },
    [setCICO, navigationSize, jumpTo],
  );

  const handleCICOJump = useCallback(
    (date: PlainDate) => {
      clearCICO();
      jumpTo(date);
    },
    [clearCICO, jumpTo],
  );

  // 予約の選択ダイアログ状態 (旧モーダル - スタンダード等の非チケットフロー)
  const {
    renderDialog: renderCheckOutDateSelectionDialog,
    openDialog: openCheckOutDateSelectionDialog,
  } = useCheckOutDateSelectionModal({
    onSelect: (value, accommodationFacilities) =>
      setSelected({
        ...value,
        accommodationFacilities,
      }),
  });

  const gtm = useGtm();

  const { focusAreaId, focusSiteId, focusAccommodationId } =
    extractFocusId(originalReservation);

  const selectedAccommodationId = selected?.accommodationId;
  const alwaysVisibleAccommodationIds = useMemo(
    () => (selectedAccommodationId ? [selectedAccommodationId] : []),
    [selectedAccommodationId],
  );

  const {
    data,
    dataAreasWithAvailability,
    loadingAvailability,
    allAreas,
    displayAreas,
    displayAreaMap,
  } = useCalendarData({
    provider: providerFlat,
    occupancyLimitTarget,
    focusAreaId,
    focusSiteId,
    focusAccommodationId,
    firstNight: debouncedFirstNight,
    lastNight: debouncedLastNight,
    minNight,
    maxNight,
    isInternal,
    cico,
    alwaysVisibleAccommodationIds,
  });

  const maybeViewer = data?.maybeViewer;
  const getDateType = useMemo(
    () =>
      resolveDateType({
        provider,
        subscription: maybeViewer?.subscription,
      }),
    [provider, maybeViewer?.subscription],
  );

  const isPeakDay = useMemo(() => {
    const peakDays = new Set(data?.peakDays ?? []);
    return (date: PlainDate) => peakDays.has(toPlainDateString(date));
  }, [data?.peakDays]);

  const reservationCyclePeriods = useMemo(
    () => maybeViewer?.subscription?.reservationCyclePeriods ?? [],
    [maybeViewer?.subscription?.reservationCyclePeriods],
  );

  // 予約作成・予約変更で共通
  //-------------------------------------------------------------
  const apollo = useApolloClient();

  // セルタップ時の挙動を上書きする拡張点（部屋詳細 Drawer 等を間に挟みたい時に Provider を置く）。
  // Provider 不在なら現行通り CheckOutDateSelectionModal を即開く。
  const flowOverride = useReservationFlowOverride();

  const handleAvailabilityCellClick: AvailabilityCellClickHandler = useCallback(
    async ({
      date,
      accommodationId,
      accommodationName,
      accommodationFacilities,
      accommodationNotices,
      selected,
    }) => {
      gtm.sendEvent("rsv_cal_cell_selected");
      if (originalReservation?.mutationScope === "OnlyExit" && selected) {
        // チェックアウト日の変更
        setSelected({
          ...selected,
          lastNight: date.subtract({ days: 1 }),
        });
        return;
      }
      if (!provider || !providerFlat) return;

      const proceedToCheckoutSelection = async () => {
        openCheckOutDateSelectionDialog({
          firstNight: date,
          selectList: await getSelectList({
            accommodationId,
            firstNight: date,
            provider: providerFlat,
            reservationId: originalReservation?.id ?? null,
            apollo,
            isInternal,
          }),
          reservationCyclePeriods,
          providerTypename: provider.__typename,
          accommodationId,
          accommodationName,
          accommodationFacilities,
          accommodationNotices,
        });
      };

      // flowOverride が存在する場合 = チケット新フロー (ReservationFlowOverrideProvider の
      // 使用箇所は ticket-room-detail-flow-bridge.tsx のみ)。
      // セル選択を bridge に委譲する。チェックアウト日選択 Drawer の組み立ては bridge 側で行い、
      // カレンダーは選択完了時に呼ばれる continuation を渡すだけ。
      // 事前の getSelectList fetch は不要 (useCheckoutAvailabilities が Drawer 内で fetch する)。
      if (flowOverride) {
        await flowOverride.onCellSelected(
          { date, accommodationId, provider: providerFlat, minNight, maxNight },
          {
            onReservationDraftSelected: (selected) => {
              setSelected({ ...selected, accommodationFacilities });
            },
          },
        );
        return;
      }

      await proceedToCheckoutSelection();
    },
    [
      gtm,
      originalReservation?.mutationScope,
      originalReservation?.id,
      provider,
      setSelected,
      openCheckOutDateSelectionDialog,
      providerFlat,
      apollo,
      isInternal,
      reservationCyclePeriods,
      flowOverride,
      minNight,
      maxNight,
    ],
  );

  const areaItems = useMemo(
    () =>
      allAreas.map((area) => ({
        slug: area.slug,
        name: area.name,
      })),
    [allAreas],
  );

  const isNativeApp = useInNativeApp();
  const noHeaderMargin = isInternal || isNativeApp;
  const pathname = usePathname();
  const isHeaderUnstickyInMobile = PAGES_WITH_UNSTICKY_HEADER_IN_MOBILE.some(
    (hiddenPath) =>
      pathname === hiddenPath || pathname.startsWith(`${hiddenPath}/`),
  );

  if (!data)
    return (
      <main className="bg-slightbeige flex min-h-full flex-col items-center">
        <div className="bg-slightbeige-108 flex h-[60px] w-full items-center justify-center"></div>
      </main>
    );

  return (
    <AccommodationFilterProvider areaItems={areaItems}>
      <CICODateSelectionModalProvider
        cico={cico}
        onConfirm={handleCICOConfirm}
        onClear={clearCICO}
        onJump={handleCICOJump}
        minNight={minNight}
        firstNight={firstNight}
        maxNight={maxNight}
        navigationSize={navigationSize}
      >
        <div className="w-full md:w-[764px]">
          {provider && !isInternal && !isSampleMode && (
            <ReservablePeriodExtendedNotice
              provider={provider}
              maxNight={maxNight}
              onJump={jumpTo}
            />
          )}
          <div
            className={clsx(
              "sticky z-10",
              noHeaderMargin
                ? "top-0"
                : isHeaderUnstickyInMobile
                  ? "md:top-global-header-md top-0"
                  : "top-global-header-min md:top-global-header-md",
            )}
          >
            <FilterBar />
            <DateNavigation
              firstNight={firstNight}
              lastNight={lastNight}
              getDateType={getDateType}
              isPeakDay={isPeakDay}
              navigationSize={navigationSize}
              minNight={minNight}
              maxNight={maxNight}
              isInternal={isInternal}
              onChevronNavigate={handleChevronNavigate}
              cico={nextCICO}
              onClearCICO={clearCICO}
              onCICOSelected={setCICO}
            />
          </div>
          <CalendarAreaList
            // カレンダー表示用データ
            loadingAvailability={loadingAvailability}
            allAreas={allAreas}
            displayAreas={displayAreas}
            displayAreaMap={displayAreaMap}
            hasAvailabilityData={dataAreasWithAvailability != null}
            selected={selected}
            providerType={providerFlat?.providerType}
            onAvailabilityCellClick={handleAvailabilityCellClick}
            originalReservationData={originalReservation}
            getDateType={getDateType}
            cico={cico}
            navigationSize={navigationSize}
            isInternal={isInternal}
            isSampleMode={isSampleMode}
          />
          <div className="visible-only-in-nativeapp h-[20px]" />
        </div>
        {renderCheckOutDateSelectionDialog()}
      </CICODateSelectionModalProvider>
    </AccommodationFilterProvider>
  );
}

export function resolveDateType({
  provider,
  subscription,
}: {
  provider: AccommodationReservationProvider | null;
  subscription: UserSubscription | null | undefined;
}): (dateString: PlainDateString | PlainDate) => DateCellDateType {
  if (
    provider?.__typename !== "StandardPlanAccommodationReservationProvider" ||
    !subscription
  ) {
    return () => NONE_DATE_TYPE;
  }

  // 外側クロージャで事前パースし、内側の per-date 呼び出しコストを削減
  const cycleEndSet = new Set(
    subscription.reservationCyclePeriods.map((p) => p.end),
  );
  const next7Period = subscription.next7DaysReservationPeriod
    ? {
        start: parsePlainDate(subscription.next7DaysReservationPeriod.start),
        end: parsePlainDate(subscription.next7DaysReservationPeriod.end),
      }
    : null;

  return (dateString: PlainDateString | PlainDate) => {
    const date =
      typeof dateString === "string" ? parsePlainDate(dateString) : dateString;

    const isCycleEnd = cycleEndSet.has(toPlainDateString(date));

    let next7: DateCellDateType["next7"] = "none";
    if (next7Period) {
      if (
        PlainDate.compare(date, next7Period.start) >= 0 &&
        PlainDate.compare(date, next7Period.end) <= 0
      ) {
        next7 = date.equals(next7Period.end) ? "end" : "in";
      }
    }

    if (!isCycleEnd && next7 === "none") return NONE_DATE_TYPE;
    return { isCycleEnd, next7 };
  };
}

function extractFocusId(
  originalReservationData: {
    mutationScope: AccommodationReservationMutationScope;
    accommodation: {
      site: { id: string; area: { id: string } };
      id: string;
    };
  } | null,
) {
  if (
    !(
      originalReservationData &&
      originalReservationData.mutationScope === "OnlyExit"
    )
  )
    return {
      focusAreaId: null,
      focusSiteId: null,
      focusAccommodationId: null,
    };
  const reservation = originalReservationData;
  return {
    focusAreaId: reservation.accommodation.site.area.id,
    focusSiteId: reservation.accommodation.site.id,
    focusAccommodationId: reservation.accommodation.id,
  };
}

const CheckOutAvailabilitiesQuery = graphql(`
  query AccommodationReservationCheckOutAvailabilities(
    $input: AccommodationReservationCheckOutAvailabilitiesInput!
    $internal: Boolean!
  ) {
    accommodationReservationCheckOutAvailabilities(
      input: $input
      internal: $internal
    ) {
      items {
        date
        available
        additionalNightsFee
        discounts {
          ruleType
          feeModifier
          feeModifierType
        }
        block @include(if: $internal) {
          id
          name
          category
          categoryInfo {
            key
            label
            categoryGroup
            note
          }
          isTentative
          note
          createdAt
          updatedAt
          createdByUser {
            name
            email
          }
          updatedByUser {
            name
            email
          }
        }
      }
    }
  }
`);

async function getSelectList({
  accommodationId,
  firstNight,
  provider,
  reservationId,
  apollo,
  isInternal = false,
}: {
  accommodationId: string;
  firstNight: PlainDate;
  provider: ReturnType<typeof buildAccommodationReservationProviderInput>;
  reservationId: string | null;
  apollo: ReturnType<typeof useApolloClient>;
  isInternal?: boolean;
}): Promise<
  {
    date: PlainDate;
    canReserve: boolean;
    additionalNightsFeeOfCycle: number;
    discounts: {
      ruleType: "NightFeeModifier" | "ConsecutiveNightDiscount" | "FreeNight";
      feeModifier: number;
      feeModifierType: "Value" | "Percentage";
    }[];
    block: BlockWithUser | null | undefined;
  }[]
> {
  const result = await apollo.query({
    query: CheckOutAvailabilitiesQuery,
    variables: {
      input: {
        provider,
        accommodationId,
        firstNight: toPlainDateString(firstNight),
        reservationIdForChange: reservationId,
      },
      internal: isInternal,
    },
  });

  const items =
    result.data?.accommodationReservationCheckOutAvailabilities.items ?? [];
  return items.map((item) => ({
    date: parsePlainDate(item.date),
    canReserve: item.available,
    additionalNightsFeeOfCycle: item.additionalNightsFee,
    discounts: item.discounts.map((d) => ({
      ruleType: d.ruleType,
      feeModifier: d.feeModifier,
      feeModifierType: d.feeModifierType,
    })),
    block: item.block,
  }));
}
